import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { Sigil } from '../_nav/Sigil';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';
import { FootballPitch } from '../_shared/FootballPitch';

// Texts: README.md §5 "01 HERO" + Igor polish 2026-05-28 (poster title, sigil swap,
//   no dots, no hairline divider — see DESIGN.md §10 + §3.4).
// Step 3 (2026-05-28): atmospheric layer behind the content — drift gradient,
//   3 frosted spheres, 2 light shafts, noise grain — all pure-CSS, see index.css
//   ".atm-*" rules and README.md §7 + §7.9.1. Decorative aerial pitch outline
//   in the bottom-right per §5 atmosphere note.
// Wave 3 (2026-06-04): 3-second WOW entry choreography (WAVE_3_BRIEF.md §3.6 + §4.1).
//   sigil → motion-stagger-1, wordmark (slide-in + mask-reveal + slow shimmer) →
//   stagger-2, subtitle → 3, body → 4, hint → 5. FootballPitch wrapper picks up
//   `module-01-pitch-pan` for a 30s breathing translateX. All anims kill themselves
//   under `prefers-reduced-motion: reduce` (DESIGN.md §7.4).

export const meta: ModuleMeta = {
  id: '01-hero',
  title: 'RAFL DIGITAL · Hero',
  status: 'approved',
  viewport: '390x844',
};

export default function HeroModule() {
  return (
    <section
      className="relative h-full w-full overflow-hidden bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={3} shafts={2} intensity="full" />

      {/* §5 atmosphere note — aerial pitch in perspective, centred behind content.
          .atm-pitch tilts the SVG via CSS perspective + rotateX(55deg) so it lays
          back into the scene. Wrapper flex-centres so the pitch sits mid-screen
          (corner placement felt off — Igor 2026-05-28). Players + animated ball
          baked into the SVG itself; see index.css `.atm-pitch-*` rules.
          Wave 3: outer wrapper carries `.module-01-pitch-pan` so the whole pitch
          drifts translateX 0 → -2vw → 0 over 30s ease-in-out infinite ("breathing"). */}
      <div
        className="module-01-pitch-pan pointer-events-none absolute inset-0 z-[1] flex items-center justify-center"
        aria-hidden
      >
        <FootballPitch
          className="atm-pitch text-text-primary opacity-[0.18]"
          style={{ width: '140vw', height: '120vh', marginTop: '18vh' }}
        />
      </div>

      <div
        className="motion-slide-in motion-stagger-1 pointer-events-none absolute left-1/2 z-10 -translate-x-1/2"
        style={{ top: 'max(var(--safe-top, 0px), 1.25rem)' }}
        aria-hidden
      >
        <Sigil variant="hero" />
      </div>

      <div
        className="relative z-10 flex h-full flex-col justify-between px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 120px)' }}
      >
        <div className="flex flex-col gap-sp-5">
          {/* Wordmark — outer wrapper handles the slide-in / stagger-2 (translateY +
              opacity). Inner h1 carries `.module-01-title-mask` (clip-path inset
              0 100% 0 0 → 0 0 0 0 over 800ms, concurrent with slide-in). The
              inner span has `.motion-shimmer` + `.module-01-title-shimmer` so the
              gradient sweep stretches to 8s (very slow, no reading conflict). Two
              wrappers because `motion-slide-in` and the mask animation both set the
              CSS `animation` shorthand and would otherwise clobber each other. */}
          <div className="motion-slide-in motion-stagger-2">
            <h1 className="module-01-title-mask font-block text-[5.5rem] leading-[0.95] tracking-[-0.02em] text-text-primary">
              <span className="motion-shimmer module-01-title-shimmer">
                RAFL<br />
                DIGITAL
              </span>
            </h1>
          </div>
          <h2 className="motion-slide-in motion-stagger-3 font-display text-[1.75rem] leading-[1.2] tracking-[-0.015em] text-text-primary">
            Геймификационные<br />
            проекты для русского<br />
            футбола
          </h2>

          <p className="motion-slide-in motion-stagger-4 max-w-[300px] font-ui text-body leading-[1.5] text-text-primary">
            Сближаем клубы и болельщиков через креативные механики на базе{' '}
            <span className="vibe">вайб/лоу/ноу</span> кодинга
          </p>
        </div>

        {/* Hint arrow — outer wrapper picks up slide-in / stagger-5. Inner div keeps
            its existing `.hint-arrow-pulse` (universal swipe-up cue). Wrapped because
            both classes target `animation` and the later cascade would otherwise win. */}
        <div className="motion-slide-in motion-stagger-5" aria-hidden>
          <div className="hint-arrow-pulse flex items-center justify-center gap-sp-3 text-text-muted">
            <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
            <span className="text-[1.25rem] leading-none">↓</span>
          </div>
        </div>
      </div>
    </section>
  );
}
